import { HttpRouting, HttpRoutingWithLocations, Domain, HttpRoutingLocation } from '@zerops/models/http-routing';
import uniq from 'lodash-es/uniq';
import partition from 'lodash-es/partition';
import { box } from 'ngrx-forms';
import { CategorizedHttpRoutings } from './http-routing-base.model';
import { HTTP_ROUTING_FORM_LOCATION_DEFAULT_VALUE } from '@zerops/zui/http-routing-form';

export const httpRoutingLocationsByStackId = (
  httpRoutings: HttpRouting[],
  serviceStackId: string,
  filterFallbackRule = false
): HttpRoutingWithLocations[] => {
  return httpRoutings
  .filter((itm) => filterFallbackRule ? itm.domains[0].domainName !== '*' : true)
  .map((itm) => ({
    ...itm,
    /**
     * Select locations that are part of this stack and locations defined on other stacks
     * for easier template consumption.
     */
    _stackLocations: itm.locations.filter((loc) => loc.serviceStackId === serviceStackId),
    _otherLocations: itm.locations.filter((loc) => loc.serviceStackId !== serviceStackId)
  }));
};

export const extractUniqDomainNamesFromDomains = (domains: Domain[] = []) => {
  return uniq(domains.map((itm) => itm.domainName));
};

export const transformLocationToFormLocations = (locations: HttpRoutingLocation[] = []) => {
  const defaults = HTTP_ROUTING_FORM_LOCATION_DEFAULT_VALUE.config;
  
  return locations.map(({ port, path, serviceStackId, config }) => ({
    path,
    portData: box({ port, serviceStackId }),
    config: {
      redirect: {
        enabled: config?.redirect?.enabled ?? defaults.redirect.enabled,
        to: config?.redirect?.to ?? defaults.redirect.to,
        code: config?.redirect?.code ?? defaults.redirect.code,
        preservePath: config?.redirect?.preservePath ?? defaults.redirect.preservePath,
        preserveQuery: config?.redirect?.preserveQuery ?? defaults.redirect.preserveQuery
      },
      basicAuth: {
        enabled: config?.basicAuth?.enabled ?? defaults.basicAuth.enabled,
        realm: config?.basicAuth?.realm ?? defaults.basicAuth.realm,
        users: config?.basicAuth?.users || defaults.basicAuth.users
      },
      accessPolicy: {
        enabled: config?.accessPolicy?.enabled ?? defaults.accessPolicy.enabled,
        defaultPolicy: config?.accessPolicy?.defaultPolicy ?? defaults.accessPolicy.defaultPolicy,
        cidr: config?.accessPolicy?.cidr ?? defaults.accessPolicy.cidr
      },
      rateLimiting: {
        enabled: config?.rateLimiting?.enabled ?? defaults.rateLimiting.enabled,
        rate: config?.rateLimiting?.rate ?? defaults.rateLimiting.rate,
        burst: config?.rateLimiting?.burst ?? defaults.rateLimiting.burst,
        zone: config?.rateLimiting?.zone ?? defaults.rateLimiting.zone,
        zoneSize: config?.rateLimiting?.zoneSize ?? defaults.rateLimiting.zoneSize,
        key: config?.rateLimiting?.key ?? defaults.rateLimiting.key
      },
      content: {
        enabled: config?.content?.enabled ?? defaults.content.enabled,
        code: config?.content?.code ?? defaults.content.code,
        content: config?.content?.content ?? defaults.content.content,
        contentType: config?.content?.contentType ?? defaults.content.contentType
      }
    }
  }));
};

export const getCategorizedHttpRoutings = (
  items: HttpRouting[],
  serviceStackId?: string
): CategorizedHttpRoutings => {

  const baseState = {
    local: [],
    external: [],
    localHasUnsynced: undefined,
    externalHasUnsynced: undefined
  };

  if (!items) { return baseState; }

  const res = items.reduce((obj, itm) => {
    if (!serviceStackId || itm.locations.some((it) => it.serviceStackId === serviceStackId)) {
      const [ _internalLocations, _externalLocations ] = partition(
        itm.locations,
        (l) => l.serviceStackId === serviceStackId
      );
      obj.local.push({
        ...itm,
        _externalLocations,
        _internalLocations,
      });
    } else {
      obj.external.push({
        ...itm,
        _externalLocations: itm.locations
      });
    }
    return obj;
  }, baseState);

  res.localHasUnsynced = res.local.some((itm: HttpRouting) => !itm.isSynced);
  res.externalHasUnsynced = res.external.some((itm: HttpRouting) => !itm.isSynced);

  return res;
};
